import { useMemo } from 'react'
import { firstTarget } from './kpiTargets'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
} from 'recharts'
import { TabProps } from './types'
import { useFormatters } from '../../contexts/PreferencesContext'
import { ChartWrapper } from './ChartWrapper'
import { Star, TrendingUp, TrendingDown, Award, Target, CheckCircle } from 'lucide-react'
import { CustomerRatingDistributionView } from './CustomerRatingDistributionView'
import { getDeliveryBand, type DeliveryThresholds } from './SiteKPIComponents'
import { roundHalfUp } from '../../lib/kpiPercent'

/** Shown when the payload carries no bands at all — never a legacy tier name. */
const UNGRADED = { color: '#9ca3af', label: 'Not graded' }

// CR-6269: a delivery percentage is graded on the customer's OTIF Grade
// Thresholds (`targets.otif_grade_bands`, the bands the dashboard colours
// with), so the label here is the one Admin Setup configured — Platinum,
// Gold, … — not a fixed Excellent / Good / Average scale at 90 / 75 / 60.
// CR-8677: graded at the decimals the score is shown with.
const gradeFor = (score: number, bands: DeliveryThresholds, decimals: number) => {
  const band = getDeliveryBand(score, bands, decimals)
  return band ? { color: band.color, label: band.label } : UNGRADED
}

interface PerformanceMetric {
  name: string
  score: number
  /** null = no target configured (CR-7714 FE-20) */
  target: number | null
  trend: 'up' | 'down' | 'stable'
  available: boolean  // CR-4211: Whether this metric has real data
}

interface HistoricalRating {
  month: string
  quality: number
  delivery: number
  overall: number
}

interface PerformanceRatingsTabProps extends TabProps {
  /** CR-6381: Whether the user is a supplier (shows own ratings) or customer (shows distribution) */
  isSupplier?: boolean
}

export function PerformanceRatingsTab({ data, currency: _currency, filters: _filters, isSupplier = true }: PerformanceRatingsTabProps) {
  // All hooks must be called before any early returns (React rules of hooks)
  const { formatNumber: _formatNumber } = useFormatters()

  // CR-6543: every number on this tab is the backend's — the same OTIF/OTD
  // the customer's dashboard shows for this supplier (cached is_otif /
  // on_time_status, tenant tolerance windows). The previous version fetched
  // the first 500 raw GRNs and compared two fields the GRN serializer does
  // not even return, so "On-Time Delivery" always rendered 0% and dragged
  // the Overall Rating down for every supplier.
  const otifScore = data?.quality?.otif ?? null
  const otdScore = data?.delivery?.otd ?? null

  const { performanceData, historicalData, radarData } = useMemo(() => {
    // Return empty data for customer view (data may be undefined)
    if (!data) {
      return { performanceData: [], historicalData: [], radarData: [] }
    }

    // Q46 / CR-7714 FE-20: the settings fields only, never an invented 90.
    const deliveryTarget = firstTarget(data.targets?.otd, data.delivery?.otdTarget)
    const overallTarget = firstTarget(data.targets?.otif, data.quality?.otifTarget)

    const metrics: PerformanceMetric[] = [
      { name: 'Overall Rating', score: otifScore ?? 0, target: overallTarget, trend: 'stable', available: otifScore !== null },
      { name: 'On-Time Delivery', score: otdScore ?? 0, target: deliveryTarget, trend: 'stable', available: otdScore !== null },
      { name: 'OTIF', score: otifScore ?? 0, target: overallTarget, trend: 'stable', available: otifScore !== null },
      // No data and no setting behind these two yet: no target either (FE-20).
      { name: 'Responsiveness', score: 0, target: null, trend: 'stable', available: false },
      { name: 'Compliance', score: 0, target: null, trend: 'stable', available: false },
    ]

    // Historical trend straight from the backend chart series (the same
    // monthly buckets the Default tab plots).
    const otifByMonth = new Map<string, number | null>(
      (data.charts?.otifData ?? []).map((p: { month: string; otif: number | null }) => [p.month, p.otif]),
    )
    const months = Array.from(new Set<string>([
      ...(data.charts?.otdData ?? []).map((p: { month: string }) => p.month),
      ...(data.charts?.otifData ?? []).map((p: { month: string }) => p.month),
    ]))
    const history: HistoricalRating[] = months.slice(-6).map((month) => {
      const otd = (data.charts?.otdData ?? []).find((p: { month: string }) => p.month === month)?.otd ?? null
      const otif = otifByMonth.get(month) ?? null
      return {
        month,
        delivery: otd !== null ? Math.round(otd * 10) / 10 : 0,
        quality: 0,
        overall: otif !== null ? Math.round(otif * 10) / 10 : 0,
      }
    })

    const radar: Array<{ subject: string; A: number; fullMark: number }> = []
    if (otdScore !== null) radar.push({ subject: 'Delivery', A: otdScore, fullMark: 100 })
    if (otifScore !== null) radar.push({ subject: 'OTIF', A: otifScore, fullMark: 100 })

    return { performanceData: metrics, historicalData: history, radarData: radar }
  }, [data, otifScore, otdScore])

  // CR-6381: Customers see rating distribution charts instead of supplier self-view
  // This must come AFTER all hooks are called (React rules of hooks)
  if (!isSupplier) {
    return <CustomerRatingDistributionView />
  }

  const displayMetrics = performanceData
  const displayHistorical = historicalData
  const displayRadar = radarData

  const gradeBands: DeliveryThresholds = data?.targets?.otif_grade_bands ?? []
  const overallRating = displayMetrics.find(m => m.name === 'Overall Rating')
  const overallStatus = gradeFor(overallRating?.score || 0, gradeBands, 0)

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-card rounded-lg shadow-sm p-4">
        <h2 className="text-lg font-semibold">Your Performance Ratings</h2>
        <p className="text-sm text-muted-foreground">
          View how your company is rated by customers across key performance indicators
        </p>
      </div>

      {/* Overall Rating Card */}
      <Card className="shadow-lg border-2" style={{ borderColor: overallStatus.color, backgroundColor: `${overallStatus.color}1A` }}>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                style={{ backgroundColor: overallStatus.color }}
              >
                {roundHalfUp(overallRating?.score || 0, 0).toFixed(0)}
              </div>
              <div>
                <h3 className="text-2xl font-bold">Overall Rating</h3>
                <p className="text-lg" style={{ color: overallStatus.color }}>{overallStatus.label}</p>
                <div className="flex items-center gap-2 mt-1">
                  <Target className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Target: {overallRating?.target == null ? 'not set' : `${overallRating.target}%`}</span>
                  {overallRating?.trend === 'up' && <TrendingUp className="h-4 w-4 text-green-500" />}
                  {overallRating?.trend === 'down' && <TrendingDown className="h-4 w-4 text-red-500" />}
                </div>
              </div>
            </div>
            <Award className="h-16 w-16" style={{ color: overallStatus.color }} />
          </div>
        </CardContent>
      </Card>

      {/* Metric Cards */}
      <div className="grid grid-cols-4 gap-4">
        {displayMetrics.filter(m => m.name !== 'Overall Rating').map((metric) => {
          const status = gradeFor(metric.score, gradeBands, 1)
          return (
            <Card key={metric.name} className="shadow-sm">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">{metric.name}</span>
                  {metric.available && metric.trend === 'up' && <TrendingUp className="h-4 w-4 text-green-500" />}
                  {metric.available && metric.trend === 'down' && <TrendingDown className="h-4 w-4 text-red-500" />}
                </div>
                {metric.available ? (
                  <>
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold" style={{ color: status.color }}>
                        {roundHalfUp(metric.score, 1).toFixed(1)}%
                      </span>
                      {metric.target !== null && <span className="text-xs text-muted-foreground">/ {metric.target}%</span>}
                    </div>
                    <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${metric.score}%`, backgroundColor: status.color }}
                      />
                    </div>
                    <p className="text-xs mt-1" style={{ color: status.color }}>{status.label}</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-muted-foreground">N/A</span>
                    </div>
                    <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden" />
                    <p className="text-xs mt-1 text-muted-foreground">Data not yet available</p>
                  </>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Performance Radar */}
        <ChartWrapper
          title="Performance Profile"
          data={displayRadar}
          exportFilename="performance-radar"
        >
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart cx="50%" cy="50%" outerRadius="80%" data={displayRadar}>
              <PolarGrid />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Radar name="Your Score" dataKey="A" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.5} />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </ChartWrapper>

        {/* Historical Trend */}
        <ChartWrapper
          title="Performance Trend"
          data={displayHistorical}
          exportFilename="performance-trend"
          legend={[
            { color: '#3b82f6', label: 'Delivery (OTD)' },
            { color: '#f59e0b', label: 'OTIF' },
          ]}
        >
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={displayHistorical}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
              <Legend />
              <Line type="monotone" dataKey="delivery" stroke="#3b82f6" strokeWidth={2} name="Delivery (OTD)" />
              <Line type="monotone" dataKey="overall" stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 5" name="OTIF" />
            </LineChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </div>

      {/* Score Comparison Bar */}
      <ChartWrapper
        title="Score vs Target Comparison"
        data={displayMetrics.filter(m => m.name !== 'Overall Rating')}
        exportFilename="score-vs-target"
        legend={[
          { color: '#3b82f6', label: 'Your Score' },
          { color: '#e5e7eb', label: 'Target' },
        ]}
      >
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={displayMetrics.filter(m => m.name !== 'Overall Rating')} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
            <Tooltip formatter={(value: number) => `${value.toFixed(1)}%`} />
            <Bar dataKey="target" fill="#e5e7eb" name="Target" />
            <Bar dataKey="score" fill="#3b82f6" name="Your Score" />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>

      {/* Tips for Improvement */}
      <Card className="shadow-sm bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Star className="h-4 w-4 text-amber-500" />
            Tips to Improve Your Rating
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Respond to PO acknowledgment requests within 24 hours to improve responsiveness score</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Submit ASNs before shipment to help customers prepare for receiving</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Keep documentation up to date (certifications, compliance documents)</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Proactively communicate any delivery date changes</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Performance ratings are calculated based on your delivery history, quality metrics,
          and responsiveness to customer requests. Ratings are updated daily.
        </p>
      </div>
    </div>
  )
}
